// A month grid over the journal, and the shared calendar the weekly Mirror's week-picker builds on.
//
// The journal list answers "what did I write"; this answers "when did I write, and when didn't I" —
// which is the question a year of entries actually raises. Days carry a dot per entry so a month reads
// as a shape at a glance: clusters, gaps, the week everything stopped.

import { useMemo, useState } from 'react';
import type { Entry } from '../api/entries';
import { IconArrow } from '../components/Icons';

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Local-midnight key. Entries are grouped by the day the writer experienced, not by UTC. */
export function dayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

interface DayCell {
  key: string;
  date: Date;
  /** Outside the displayed month — rendered faintly to keep the grid rectangular. */
  outside: boolean;
  entries: Entry[];
}

/**
 * Six weeks starting on the Monday on or before the 1st.
 *
 * Always six rather than however many the month needs, so the grid does not change height as you page
 * through — a calendar that reflows on every arrow press is unpleasant to scan.
 */
function buildMonth(year: number, month: number, byDay: Map<string, Entry[]>): DayCell[] {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const start = new Date(year, month, 1 - offset);

  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const key = dayKey(date);
    return { key, date, outside: date.getMonth() !== month, entries: byDay.get(key) ?? [] };
  });
}

export function JournalCalendar({
  entries,
  selected,
  onSelect,
}: {
  entries: Entry[];
  /** Day key of the currently open day, or null for "the whole month". */
  selected: string | null;
  onSelect: (day: string | null) => void;
}) {
  // Opens on the month of the newest entry rather than today, so a journal picked up after a long
  // pause lands on writing instead of an empty grid.
  const [cursor, setCursor] = useState(() => {
    const newest = entries[0]?.createdAt;
    const base = newest ? new Date(newest) : new Date();
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  const byDay = useMemo(() => {
    const map = new Map<string, Entry[]>();
    for (const entry of entries) {
      const key = dayKey(new Date(entry.createdAt));
      const list = map.get(key);
      if (list) list.push(entry);
      else map.set(key, [entry]);
    }
    return map;
  }, [entries]);

  const cells = useMemo(() => buildMonth(cursor.year, cursor.month, byDay), [cursor, byDay]);
  const todayKey = dayKey(new Date());

  const shift = (by: number) => {
    setCursor((c) => {
      const next = new Date(c.year, c.month + by, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  const written = cells.filter((c) => !c.outside && c.entries.length > 0).length;

  return (
    <div className="ocal">
      <div className="ocal-head">
        <button className="ocal-nav" onClick={() => shift(-1)} aria-label="Previous month">
          <IconArrow style={{ transform: 'rotate(180deg)' }} />
        </button>
        <div className="ocal-month">
          <span className="ocal-name">{MONTHS[cursor.month]}</span>
          <span className="omono ocal-year">{cursor.year}</span>
        </div>
        <button className="ocal-nav" onClick={() => shift(1)} aria-label="Next month">
          <IconArrow />
        </button>
      </div>

      <div className="ocal-grid" role="grid">
        {WEEKDAYS.map((day, i) => (
          <div className="ocal-dow" key={i} aria-hidden="true">{day}</div>
        ))}

        {cells.map((cell) => {
          const has = cell.entries.length > 0;
          const dreams = cell.entries.filter((e) => e.kind === 'dream').length;
          return (
            <button
              key={cell.key}
              className={[
                'ocal-day',
                cell.outside ? 'outside' : '',
                has ? 'has' : '',
                cell.key === todayKey ? 'today' : '',
                cell.key === selected ? 'sel' : '',
              ].filter(Boolean).join(' ')}
              // A day with nothing in it is still worth pressing — it is how you see that a gap is
              // real rather than a filter hiding something.
              onClick={() => onSelect(cell.key === selected ? null : cell.key)}
              aria-label={`${cell.date.toDateString()}, ${cell.entries.length} ${cell.entries.length === 1 ? 'entry' : 'entries'}`}
              aria-pressed={cell.key === selected}
            >
              <span className="ocal-n">{cell.date.getDate()}</span>
              {has ? (
                <span className="ocal-dots">
                  {/* Capped at three: past that the dots stop being countable and start being noise. */}
                  {cell.entries.slice(0, 3).map((entry, i) => (
                    <i key={i} className={entry.kind === 'dream' ? 'dream' : 'waking'} />
                  ))}
                </span>
              ) : null}
              {dreams > 0 && cell.entries.length > 3 ? <span className="ocal-more" /> : null}
            </button>
          );
        })}
      </div>

      <div className="ocal-foot">
        <span className="omono">
          {written} {written === 1 ? 'day' : 'days'} written in {MONTHS[cursor.month]}
        </span>
        {selected ? (
          <button className="ocal-clear" onClick={() => onSelect(null)}>Show the whole month</button>
        ) : null}
      </div>
    </div>
  );
}
